import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../types/ApiCallType'
import { resetProxy } from '../utils/proxyUtils'
import { FormFieldNamesEnum } from '../../routes/app/settings/account/NewUserForm/formFieldNamesEnum'
import { DEFAULT_LIMIT } from '../utils/apiUtils'
import { GlobalSortDirectionEnum } from '../types/GlobalSortDirectionEnum'

export const FIELDS_REQUIRED_FOR_SUBMISSION = [
  FormFieldNamesEnum.FirstName,
  FormFieldNamesEnum.LastName,
  FormFieldNamesEnum.Email
]

export enum MerchantUsersTableEnum {
  Id = 'id',
  FirstName = 'firstName',
  LastName = 'lastName',
  Email = 'email',
  IsMerchantAdmin = 'isMerchantAdmin'
}

export interface Pagination {
  totalCount: number
  limit: number
  offset: number
}

export interface Filters {
  offset: number
  sortColumn: MerchantUsersTableEnum
  sortDirection: GlobalSortDirectionEnum
}

export interface MinimumUser {
  id: number
  firstName: string
  lastName: string
  email: string
  isAdmin: boolean
}

interface NewUserProxy {
  allowSubmit: boolean
  canCreateUsers: boolean
  displayUserAddModal: boolean
  displayUserDeleteModal: boolean
  displayUserEditModal: boolean
  showForms: boolean
  apiCall: ApiCallType
  emailValidationCall: ApiCallType
  deleteUserApiCall: ApiCallType
  editUserApiCall: ApiCallType
  filters: Filters
  hasMore: boolean
  completed: boolean
  formValues: { [key in FormFieldNamesEnum]?: string | boolean | null }
  formErrors: { [key in FormFieldNamesEnum]?: string | null }
  formDirtied: readonly FormFieldNamesEnum[]
  formBlurred: readonly FormFieldNamesEnum[]
  pagination: Pagination
  merchantUsers: MinimumUser[]
  selectedUser: MinimumUser | null
  showEmailError: boolean
  showFirstNameError: boolean
  showLastNameError: boolean
}

const initialValues = {
  apiCall: {
    error: null,
    loading: false
  },
  canCreateUsers: false,
  completed: false,
  deleteUserApiCall: {
    error: null,
    loading: false
  },
  displayUserAddModal: false,
  displayUserDeleteModal: false,
  displayUserEditModal: false,
  editUserApiCall: {
    error: null,
    loading: false
  },
  emailValidationCall: {
    error: null,
    loading: false
  },
  filters: {
    offset: 0,
    sortColumn: MerchantUsersTableEnum.Id,
    sortDirection: GlobalSortDirectionEnum.Ascending
  },
  formBlurred: [],
  formDirtied: [],
  formErrors: {},
  formValues: {},
  merchantUsers: [],
  pagination: {
    limit: DEFAULT_LIMIT,
    offset: 0,
    totalCount: 0
  },
  selectedUser: null,
  showForms: false
}

const baseProxy = proxy(initialValues)

const showErrorFunctionGenerator = (fieldName: FormFieldNamesEnum) => (get: any) => {
  const { formDirtied, formBlurred, formErrors } = get(baseProxy)
  const isDirty = (formDirtied as string[]).includes(fieldName)
  const isBlurred = (formBlurred as string[]).includes(fieldName)
  const isErrored = Boolean((formErrors as { [key in FormFieldNamesEnum]?: string | null })[fieldName])
  return isDirty && isBlurred && isErrored
}

export const proxyNewUser = derive({
  allowSubmit: (get) => {
    const { apiCall: { loading, error }, emailValidationCall, formDirtied, formErrors } = get(baseProxy)
    const noErrors = Object.values(formErrors).filter(x => x).length === 0
    // @ts-expect-error
    const allDirtied = FIELDS_REQUIRED_FOR_SUBMISSION.every((x: string) => formDirtied.includes(x))
    return noErrors && allDirtied && !loading && !error && !emailValidationCall.loading
  },
  hasMore: get => {
    const { merchantUsers, pagination } = get(baseProxy)
    const totalCount = pagination?.totalCount ?? 0
    return merchantUsers.length < totalCount
  },
  showEmailError: showErrorFunctionGenerator(FormFieldNamesEnum.Email),
  showFirstNameError: showErrorFunctionGenerator(FormFieldNamesEnum.FirstName),
  showLastNameError: showErrorFunctionGenerator(FormFieldNamesEnum.LastName)
}, { proxy: baseProxy }) as NewUserProxy

devtools(proxyNewUser, { name: 'proxyNewUser' })

export function resetFormValues (): void {
  proxyNewUser.emailValidationCall.error = null
  proxyNewUser.selectedUser = null
  proxyNewUser.formValues = {}
  proxyNewUser.formErrors = {}
  proxyNewUser.formDirtied = []
  proxyNewUser.formBlurred = []
}

export function resetProxyNewUser (): void {
  resetProxy(initialValues, proxyNewUser)
}
